/*
 * @Author: Ryan
 * @Date: 2025-12-05
 * @Description: 视频缓存数据库 - 使用 IndexedDB 存储视频 Blob
 */

const DB_NAME = 'HeroVideoCache';
const STORE_NAME = 'videos';
const DB_VERSION = 1;
const CACHE_EXPIRY_DAYS = 7; // 缓存有效期 7 天

interface CachedVideo {
  url: string;
  blob: Blob;
  timestamp: number;
}

class VideoCacheDB {
  private db: IDBDatabase | null = null;
  private initPromise: Promise<void> | null = null;

  // 初始化数据库
  async init(): Promise<void> {
    if (this.db) return;
    if (this.initPromise) return this.initPromise;

    this.initPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        console.error('Failed to open IndexedDB:', request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME, { keyPath: 'url' });
        }
      };
    });

    return this.initPromise;
  }

  // 检查缓存是否过期
  private isExpired(timestamp: number): boolean {
    const now = Date.now();
    const expiryTime = CACHE_EXPIRY_DAYS * 24 * 60 * 60 * 1000;
    return now - timestamp > expiryTime;
  }

  // 获取缓存的视频
  async get(url: string): Promise<Blob | null> {
    try {
      await this.init();
      if (!this.db) return null;

      return new Promise((resolve, reject) => {
        const transaction = this.db!.transaction(STORE_NAME, 'readonly');
        const store = transaction.objectStore(STORE_NAME);
        const request = store.get(url);

        request.onsuccess = () => {
          const result = request.result as CachedVideo | undefined;
          if (result && !this.isExpired(result.timestamp)) {
            resolve(result.blob);
          } else {
            // 过期或不存在
            if (result) {
              // 删除过期缓存
              this.delete(url);
            }
            resolve(null);
          }
        };

        request.onerror = () => {
          console.error('Failed to get cached video:', request.error);
          reject(request.error);
        };
      });
    } catch (error) {
      console.error('Error getting cached video:', error);
      return null;
    }
  }

  // 保存视频到缓存
  async set(url: string, blob: Blob): Promise<void> {
    try {
      await this.init();
      if (!this.db) return;

      return new Promise((resolve, reject) => {
        const transaction = this.db!.transaction(STORE_NAME, 'readwrite');
        const store = transaction.objectStore(STORE_NAME);
        const data: CachedVideo = {
          url,
          blob,
          timestamp: Date.now(),
        };
        const request = store.put(data);

        request.onsuccess = () => resolve();
        request.onerror = () => {
          console.error('Failed to cache video:', request.error);
          reject(request.error);
        };
      });
    } catch (error) {
      console.error('Error caching video:', error);
    }
  }

  // 删除缓存
  async delete(url: string): Promise<void> {
    try {
      await this.init();
      if (!this.db) return;

      return new Promise((resolve, reject) => {
        const transaction = this.db!.transaction(STORE_NAME, 'readwrite');
        const store = transaction.objectStore(STORE_NAME);
        const request = store.delete(url);

        request.onsuccess = () => resolve();
        request.onerror = () => {
          console.error('Failed to delete cached video:', request.error);
          reject(request.error);
        };
      });
    } catch (error) {
      console.error('Error deleting cached video:', error);
    }
  }

  // 清空所有缓存
  async clear(): Promise<void> {
    try {
      await this.init();
      if (!this.db) return;

      return new Promise((resolve, reject) => {
        const transaction = this.db!.transaction(STORE_NAME, 'readwrite');
        const store = transaction.objectStore(STORE_NAME);
        const request = store.clear();

        request.onsuccess = () => resolve();
        request.onerror = () => {
          console.error('Failed to clear cache:', request.error);
          reject(request.error);
        };
      });
    } catch (error) {
      console.error('Error clearing cache:', error);
    }
  }
}

// 导出单例
export const videoCacheDB = new VideoCacheDB();
